/**
 * recipe-engagement.js — Aurum & Ash
 *
 * Owns Save / Made state for recipes. Source of truth on-device is
 * localStorage. Klaviyo events fire on every change for marketing flows
 * (see Task 3). Customer metafield hydration (cross-device sync) is
 * read from window.aaCustomerRecipes when present.
 *
 * Public API (window.AurumAsh.engagement):
 *   getSaved()  -> string[] of recipe handles
 *   getMade()   -> string[] of recipe handles
 *   isSaved(h)  -> boolean
 *   isMade(h)   -> boolean
 *   toggleSave(handle, meta)
 *   toggleMade(handle, meta)
 *   on(event, handler)  -> 'change' fires after any toggle
 */
(function () {
  'use strict';

  var AA = window.AurumAsh = window.AurumAsh || {};
  if (AA.engagement) return;

  var SAVED_KEY = 'aa_saved_recipes';
  var MADE_KEY = 'aa_made_recipes';
  var listeners = [];

  function readList(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  }

  function writeList(key, list) {
    try { localStorage.setItem(key, JSON.stringify(list)); }
    catch (e) { /* quota / private mode — fail silent */ }
  }

  function emit(eventName, payload) {
    listeners.forEach(function (l) {
      if (l.event === eventName) {
        try { l.handler(payload); } catch (e) { /* swallow */ }
      }
    });
  }

  function getSaved() { return readList(SAVED_KEY); }
  function getMade() { return readList(MADE_KEY); }
  function isSaved(h) { return getSaved().indexOf(h) !== -1; }
  function isMade(h) { return getMade().indexOf(h) !== -1; }

  function toggle(key, handle, meta) {
    var list = readList(key);
    var idx = list.indexOf(handle);
    var nowOn;
    if (idx === -1) { list.push(handle); nowOn = true; }
    else { list.splice(idx, 1); nowOn = false; }
    writeList(key, list);
    return nowOn;
  }

  function fireKlaviyo(eventName, handle, meta) {
    if (typeof window._learnq === 'undefined') return;
    try {
      window._learnq.push(['track', eventName, {
        RecipeHandle: handle,
        RecipeTitle: meta && meta.title,
        RecipeType: meta && meta.type,
        SpiritCategory: meta && meta.category,
        Method: meta && meta.method,
        ImageUrl: meta && meta.image,
        RecipeUrl: meta && meta.url,
        $value: 0
      }]);
    } catch (e) { /* swallow */ }
  }

  // GA4 — direct gtag call (Google channel loads gtag un-sandboxed on the storefront).
  function fireGA4(eventName, handle, meta) {
    if (typeof window.gtag !== 'function') return;
    try {
      window.gtag('event', eventName, {
        recipe_handle: handle,
        recipe_title: meta && meta.title,
        recipe_type: meta && meta.type,
        recipe_method: meta && meta.method,
        spirit_category: meta && meta.category
      });
    } catch (e) { /* swallow */ }
  }

  // Meta bridge — publish a Shopify custom event; the Meta custom web pixel relays it to fbq.
  function firePixel(eventName, handle, meta) {
    if (!window.Shopify || !window.Shopify.analytics ||
        typeof window.Shopify.analytics.publish !== 'function') return;
    try {
      window.Shopify.analytics.publish(eventName, {
        recipeHandle: handle,
        recipeTitle: meta && meta.title,
        recipeType: meta && meta.type,
        recipeMethod: meta && meta.method,
        spiritCategory: meta && meta.category,
        recipeUrl: meta && meta.url,
        imageUrl: meta && meta.image
      });
    } catch (e) { /* swallow */ }
  }

  // Lazy-built lookup of recipe handle -> { spirit_category, method, type, name }
  var recipeIndexCache = null;
  function getRecipeIndex() {
    if (recipeIndexCache !== null) return recipeIndexCache;
    var lookup = {};
    var el = document.getElementById('aa-recipes-index');
    if (el) {
      try {
        var list = JSON.parse(el.textContent) || [];
        for (var i = 0; i < list.length; i++) {
          var r = list[i];
          if (r && r.handle) lookup[r.handle] = r;
        }
      } catch (e) { /* swallow */ }
    }
    recipeIndexCache = lookup;
    return lookup;
  }

  function topKey(counts) {
    var best = '', bestN = 0;
    for (var k in counts) {
      if (counts.hasOwnProperty(k) && counts[k] > bestN) { best = k; bestN = counts[k]; }
    }
    return best;
  }

  function deriveProperties(handles) {
    var index = getRecipeIndex();
    var bySpirit = {}, byMethod = {}, byType = {};
    for (var i = 0; i < handles.length; i++) {
      var meta = index[handles[i]];
      if (!meta) continue;
      var s = meta.spirit_category || 'none';
      var m = meta.method || 'none';
      var t = meta.type || 'cocktail';
      bySpirit[s] = (bySpirit[s] || 0) + 1;
      byMethod[m] = (byMethod[m] || 0) + 1;
      byType[t] = (byType[t] || 0) + 1;
    }
    return {
      by_spirit: bySpirit,
      by_method: byMethod,
      by_type: byType,
      favorite_spirit: topKey(bySpirit),
      favorite_method: topKey(byMethod),
      favorite_type: topKey(byType)
    };
  }

  function syncKlaviyoProfile(kind) {
    if (typeof window._learnq === 'undefined') return;
    try {
      var saved = getSaved();
      var made = getMade();
      var savedD = deriveProperties(saved);
      var madeD = deriveProperties(made);
      var nowISO = new Date().toISOString();
      var payload = {
        saved_recipes: saved,
        made_recipes: made,
        saves_count: saved.length,
        made_count: made.length,
        saves_by_spirit: savedD.by_spirit,
        saves_by_method: savedD.by_method,
        saves_by_type: savedD.by_type,
        favorite_spirit_saved: savedD.favorite_spirit,
        favorite_method_saved: savedD.favorite_method,
        favorite_type_saved: savedD.favorite_type,
        made_by_spirit: madeD.by_spirit,
        made_by_method: madeD.by_method,
        made_by_type: madeD.by_type,
        favorite_spirit_made: madeD.favorite_spirit,
        favorite_method_made: madeD.favorite_method,
        favorite_type_made: madeD.favorite_type
      };
      if (kind === 'save') payload.last_saved_at = nowISO;
      if (kind === 'made') payload.last_made_at = nowISO;
      window._learnq.push(['identify', payload]);
    } catch (e) { /* swallow */ }
  }

  function toggleSave(handle, meta) {
    var nowOn = toggle(SAVED_KEY, handle, meta);
    fireKlaviyo(nowOn ? 'Saved Recipe' : 'Unsaved Recipe', handle, meta);
    fireGA4(nowOn ? 'recipe_save' : 'recipe_unsave', handle, meta);
    if (nowOn) firePixel('aurum_ash:recipe_saved', handle, meta);
    syncKlaviyoProfile(nowOn ? 'save' : null);
    emit('change', { kind: 'save', handle: handle, on: nowOn, meta: meta, list: getSaved() });
    return nowOn;
  }

  function toggleMade(handle, meta) {
    var nowOn = toggle(MADE_KEY, handle, meta);
    fireKlaviyo(nowOn ? 'Made Recipe' : 'Unmade Recipe', handle, meta);
    fireGA4(nowOn ? 'recipe_made' : 'recipe_unmade', handle, meta);
    syncKlaviyoProfile(nowOn ? 'made' : null);
    emit('change', { kind: 'made', handle: handle, on: nowOn, meta: meta, list: getMade() });
    return nowOn;
  }

  function on(eventName, handler) { listeners.push({ event: eventName, handler: handler }); }

  // ------------------------------------------------------------------
  // DOM wiring: paint state + handle clicks
  // ------------------------------------------------------------------
  function paintButton(btn) {
    var handle = btn.dataset.recipeHandle;
    if (!handle) return;
    var on;
    if (btn.hasAttribute('data-recipe-save')) on = isSaved(handle);
    else if (btn.hasAttribute('data-recipe-made')) on = isMade(handle);
    else return;
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    var labelEl = btn.querySelector('.aa-recipe-save__label, .aa-recipe-made__label');
    if (labelEl) {
      if (btn.hasAttribute('data-recipe-save')) labelEl.textContent = on ? 'Saved' : 'Save';
      else labelEl.textContent = on ? 'Made it' : 'I made this';
    }
    if (btn.hasAttribute('data-recipe-save') && !labelEl) {
      btn.setAttribute('aria-label', (on ? 'Unsave ' : 'Save ') + (btn.dataset.recipeTitle || 'recipe'));
    }
  }

  function paintAll() {
    document.querySelectorAll('[data-recipe-save], [data-recipe-made]').forEach(paintButton);
    paintMyRecipes();
  }

  function paintMyRecipes() {
    var panel = document.querySelector('[data-my-recipes]');
    if (!panel) return;
    var grid = panel.querySelector('[data-my-grid]');
    var emptyEl = panel.querySelector('[data-my-empty]');
    var activeTab = panel.querySelector('[data-my-tab][aria-selected="true"]');
    var mode = activeTab ? activeTab.dataset.myTab : 'saved';
    var list = mode === 'made' ? getMade() : getSaved();
    var setLookup = {};
    list.forEach(function (h) { setLookup[h] = true; });

    var anyVisible = false;
    grid.querySelectorAll('[data-my-recipe-handle]').forEach(function (cardWrap) {
      var on = !!setLookup[cardWrap.dataset.myRecipeHandle];
      cardWrap.hidden = !on;
      if (on) anyVisible = true;
    });

    panel.querySelector('[data-my-count="saved"]').textContent = getSaved().length;
    panel.querySelector('[data-my-count="made"]').textContent = getMade().length;

    if (emptyEl) {
      emptyEl.hidden = anyVisible;
      emptyEl.textContent = anyVisible
        ? ''
        : (mode === 'made'
            ? "You haven't marked any recipes as made yet."
            : "You haven't saved any recipes yet. Browse the catalog.");
    }
  }

  function initMyRecipesTabs() {
    var panel = document.querySelector('[data-my-recipes]');
    if (!panel) return;
    panel.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-my-tab]');
      if (!tab) return;
      panel.querySelectorAll('[data-my-tab]').forEach(function (t) {
        var active = t === tab;
        t.classList.toggle('aa-chip--active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      paintMyRecipes();
    });
  }

  function readMeta(btn) {
    return {
      title: btn.dataset.recipeTitle || '',
      type: btn.dataset.recipeType || '',
      category: btn.dataset.recipeCategory || '',
      method: btn.dataset.recipeMethod || '',
      image: btn.dataset.recipeImage || '',
      url: btn.dataset.recipeUrl || ''
    };
  }

  function onClick(e) {
    var btn = e.target.closest('[data-recipe-save], [data-recipe-made]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = btn.dataset.recipeHandle;
    if (!handle) return;
    if (btn.hasAttribute('data-recipe-save')) toggleSave(handle, readMeta(btn));
    else toggleMade(handle, readMeta(btn));
    paintAll();
  }

  function identifyIfCustomer() {
    if (typeof window._learnq === 'undefined') return;
    if (!window.aaCustomer || !window.aaCustomer.email) return;
    try {
      var saved = getSaved();
      var made = getMade();
      var savedD = deriveProperties(saved);
      var madeD = deriveProperties(made);
      window._learnq.push(['identify', {
        $email: window.aaCustomer.email,
        $first_name: window.aaCustomer.firstName,
        $last_name: window.aaCustomer.lastName,
        saved_recipes: saved,
        made_recipes: made,
        saves_count: saved.length,
        made_count: made.length,
        saves_by_spirit: savedD.by_spirit,
        saves_by_method: savedD.by_method,
        saves_by_type: savedD.by_type,
        favorite_spirit_saved: savedD.favorite_spirit,
        favorite_method_saved: savedD.favorite_method,
        favorite_type_saved: savedD.favorite_type,
        made_by_spirit: madeD.by_spirit,
        made_by_method: madeD.by_method,
        made_by_type: madeD.by_type,
        favorite_spirit_made: madeD.favorite_spirit,
        favorite_method_made: madeD.favorite_method,
        favorite_type_made: madeD.favorite_type
      }]);
    } catch (e) { /* swallow */ }
  }

  function hydrateFromMetafield() {
    if (!window.aaCustomerRecipes) return;
    var serverSaved = Array.isArray(window.aaCustomerRecipes.saved) ? window.aaCustomerRecipes.saved : [];
    var serverMade = Array.isArray(window.aaCustomerRecipes.made) ? window.aaCustomerRecipes.made : [];
    // Union local + server, prefer server's presence for cross-device sync.
    var localSaved = getSaved();
    var localMade = getMade();
    var mergedSaved = serverSaved.slice();
    localSaved.forEach(function (h) { if (mergedSaved.indexOf(h) === -1) mergedSaved.push(h); });
    var mergedMade = serverMade.slice();
    localMade.forEach(function (h) { if (mergedMade.indexOf(h) === -1) mergedMade.push(h); });
    writeList(SAVED_KEY, mergedSaved);
    writeList(MADE_KEY, mergedMade);
  }

  function init() {
    hydrateFromMetafield();
    identifyIfCustomer();
    paintAll();
    initMyRecipesTabs();
    document.addEventListener('click', onClick);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  AA.engagement = {
    getSaved: getSaved, getMade: getMade,
    isSaved: isSaved, isMade: isMade,
    toggleSave: toggleSave, toggleMade: toggleMade,
    on: on, _paintAll: paintAll
  };
})();
